import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, Users, TrendingUp, DollarSign, Activity, Clock,
  Eye, CheckCircle2, AlertTriangle, RefreshCw, Crown,
  BarChart3, ArrowUpRight, ArrowDownRight, Search,
  Target, Briefcase, Globe, Award, Zap, PieChart, ShoppingCart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart as RechartsPie, Pie, Cell
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SalesPlatformAdmin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('30d');
  
  // Dashboard data
  const [stats, setStats] = useState({
    totalRevenue: 245890,
    totalDeals: 156,
    conversionRate: 23.5,
    avgDealSize: 1576,
    activeLeads: 342,
    closedDealsThisMonth: 28,
    pipelineValue: 1250000,
    teamPerformance: 87
  });
  
  const [salesData, setSalesData] = useState([
    { month: 'Jan', revenue: 45000, deals: 12, leads: 45 },
    { month: 'Feb', revenue: 52000, deals: 15, leads: 52 },
    { month: 'Mar', revenue: 48000, deals: 11, leads: 38 },
    { month: 'Apr', revenue: 61000, deals: 18, leads: 67 },
    { month: 'May', revenue: 55000, deals: 14, leads: 48 },
    { month: 'Jun', revenue: 67000, deals: 21, leads: 72 },
  ]);
  
  const [pipelineData, setPipelineData] = useState([
    { name: 'Prospecting', value: 45, color: '#3b82f6' },
    { name: 'Qualification', value: 32, color: '#8b5cf6' },
    { name: 'Proposal', value: 28, color: '#f59e0b' },
    { name: 'Negotiation', value: 18, color: '#10b981' },
    { name: 'Closed Won', value: 23, color: '#22c55e' },
  ]);
  
  const [topSalesReps, setTopSalesReps] = useState([
    { id: 1, name: 'Sarah Johnson', deals: 28, revenue: 89500, target: 80000, performance: 112 },
    { id: 2, name: 'Michael Chen', deals: 24, revenue: 76200, target: 75000, performance: 102 },
    { id: 3, name: 'Emily Davis', deals: 22, revenue: 71800, target: 70000, performance: 103 },
    { id: 4, name: 'James Wilson', deals: 19, revenue: 62400, target: 65000, performance: 96 },
    { id: 5, name: 'Lisa Anderson', deals: 17, revenue: 55200, target: 60000, performance: 92 },
  ]);
  
  const [recentDeals, setRecentDeals] = useState([
    { id: 1, company: 'TechCorp Industries', value: 45000, stage: 'Closed Won', rep: 'Sarah J.', date: '2026-02-10' },
    { id: 2, company: 'Global Solutions Ltd', value: 28500, stage: 'Negotiation', rep: 'Michael C.', date: '2026-02-09' },
    { id: 3, company: 'Innovation Labs', value: 67000, stage: 'Proposal', rep: 'Emily D.', date: '2026-02-08' },
    { id: 4, company: 'Enterprise Group', value: 32000, stage: 'Qualification', rep: 'James W.', date: '2026-02-07' },
    { id: 5, company: 'Digital Dynamics', value: 54000, stage: 'Closed Won', rep: 'Lisa A.', date: '2026-02-06' },
  ]);

  useEffect(() => {
    fetchSalesData();
  }, [dateFilter]);

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch real data from Sales API
      const [statsRes, teamRes, leadsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/sales/reports/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/sales/admin/team-members`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/sales/leads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/sales/contracts`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      // Update stats if available
      if (statsRes.data) {
        const apiStats = statsRes.data;
        setStats(prev => ({
          ...prev,
          totalRevenue: apiStats.total_pipeline_value || prev.totalRevenue,
          totalDeals: apiStats.total_leads || prev.totalDeals,
          activeLeads: apiStats.total_leads || prev.activeLeads,
          pipelineValue: apiStats.total_pipeline_value || prev.pipelineValue
        }));
        
        // Update pipeline data from API
        if (apiStats.pipeline_stats) {
          const pipelineColors = {
            prospect: '#3b82f6',
            contacted: '#8b5cf6', 
            negotiation: '#f59e0b',
            contract: '#10b981',
            closed: '#22c55e'
          };
          const newPipelineData = Object.entries(apiStats.pipeline_stats).map(([name, value]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            value: value,
            color: pipelineColors[name] || '#6b7280'
          }));
          if (newPipelineData.some(p => p.value > 0)) {
            setPipelineData(newPipelineData);
          }
        }
      }
      
      // Update team leaderboard from API
      if (teamRes.data && teamRes.data.length > 0) {
        const teamLeaderboard = teamRes.data.map((member, idx) => ({
          id: member.user?.id || idx,
          name: member.user?.name || 'Unknown',
          deals: member.stats?.total_leads || 0,
          revenue: (member.stats?.total_leads || 0) * 1500, // Estimate
          target: 60000,
          performance: Math.round(((member.stats?.total_leads || 0) / 20) * 100)
        })).sort((a, b) => b.deals - a.deals).slice(0, 5);
        
        if (teamLeaderboard.length > 0) {
          setTopSalesReps(teamLeaderboard);
        }
      }
      
      // Update recent deals from contracts
      if (contractsRes.data && contractsRes.data.length > 0) {
        const recentContractDeals = contractsRes.data.slice(0, 5).map((contract, idx) => ({
          id: contract.id || idx,
          company: contract.client_name || contract.title || 'Unknown Client',
          value: contract.value || 0,
          stage: contract.status === 'signed' ? 'Closed Won' : 
                 contract.status === 'pending' ? 'Negotiation' : 'Proposal',
          rep: contract.assigned_to || 'Unassigned',
          date: contract.created_at?.split('T')[0] || new Date().toISOString().split('T')[0]
        }));
        setRecentDeals(recentContractDeals);
      }
      
    } catch (error) {
      console.error('Error fetching sales data:', error);
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStageColor = (stage) => {
    const colors = {
      'Prospecting': 'bg-blue-100 text-blue-800',
      'Qualification': 'bg-purple-100 text-purple-800',
      'Proposal': 'bg-amber-100 text-amber-800',
      'Negotiation': 'bg-orange-100 text-orange-800',
      'Closed Won': 'bg-green-100 text-green-800',
      'Closed Lost': 'bg-red-100 text-red-800',
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  // Access check - Super Admin, Platform Admin, and Sales Admin can access
  if (user?.role !== 'platform_admin' && user?.role !== 'super_admin' && user?.sales_role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <Crown className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold">Access Restricted</h3>
            <p className="text-muted-foreground">Platform Admin access required</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="sales-platform-admin">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Crown className="w-8 h-8 text-amber-500" />
            Sales Platform Admin
          </h1>
          <p className="text-muted-foreground mt-1">
            Complete oversight of sales operations, team performance, and pipeline health
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="1y">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchSalesData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                  <div className="flex items-center text-green-600 text-sm mt-1">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>12.5% vs last month</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pipeline Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.pipelineValue)}</p>
                  <div className="flex items-center text-blue-600 text-sm mt-1">
                    <Target className="w-4 h-4 mr-1" />
                    <span>146 active deals</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-2xl font-bold">{stats.conversionRate}%</p>
                  <div className="flex items-center text-amber-600 text-sm mt-1">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    <span>+2.3% improvement</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Team Performance</p>
                  <p className="text-2xl font-bold">{stats.teamPerformance}%</p>
                  <div className="flex items-center text-purple-600 text-sm mt-1">
                    <Users className="w-4 h-4 mr-1" />
                    <span>Above target</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="team">Team Performance</TabsTrigger>
          <TabsTrigger value="deals">Recent Deals</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={salesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `${value/1000}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Deals vs Leads */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Deals vs Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="leads" fill="#3b82f6" name="Leads" />
                      <Bar dataKey="deals" fill="#10b981" name="Closed Deals" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pipeline Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Pipeline Distribution</CardTitle>
                <CardDescription>Deals by stage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={pipelineData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {pipelineData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Pipeline Stages */}
            <Card>
              <CardHeader>
                <CardTitle>Stage Breakdown</CardTitle>
                <CardDescription>Deals in each stage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pipelineData.map((stage) => (
                    <div key={stage.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                        <span className="font-medium">{stage.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-muted-foreground">{stage.value} deals</span>
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${(stage.value / 50) * 100}%`,
                              backgroundColor: stage.color
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sales Team Leaderboard</CardTitle>
              <CardDescription>Performance ranking for the current period</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Sales Rep</TableHead>
                    <TableHead className="text-right">Deals Closed</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Performance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topSalesReps.map((rep, index) => (
                    <TableRow key={rep.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {index === 0 && <Award className="w-4 h-4 text-amber-500" />}
                          {index === 1 && <Award className="w-4 h-4 text-gray-400" />}
                          {index === 2 && <Award className="w-4 h-4 text-amber-700" />}
                          <span className="font-medium">#{index + 1}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{rep.name}</TableCell>
                      <TableCell className="text-right">{rep.deals}</TableCell>
                      <TableCell className="text-right">{formatCurrency(rep.revenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatCurrency(rep.target)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={rep.performance >= 100 ? 'default' : 'secondary'}>
                          {rep.performance}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Deals</CardTitle>
                <CardDescription>Latest deal activities</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search deals..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-[200px]"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Deal Value</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Sales Rep</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentDeals
                    .filter(deal => deal.company.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((deal) => (
                      <TableRow key={deal.id}>
                        <TableCell className="font-medium">{deal.company}</TableCell>
                        <TableCell className="text-right">{formatCurrency(deal.value)}</TableCell>
                        <TableCell>
                          <Badge className={getStageColor(deal.stage)}>
                            {deal.stage}
                          </Badge>
                        </TableCell>
                        <TableCell>{deal.rep}</TableCell>
                        <TableCell className="text-muted-foreground">{deal.date}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalesPlatformAdmin;
